import { Injectable, inject } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import type { AppError } from 'src/app/core/models/app-error.model';
import { AttachmentError, AttachmentErrorType } from '../../domain/errors/attachment-error';
import { CaseEntity } from '../../domain/entities/case.entity';
import { CaseListItemEntity } from '../../domain/entities/case-list-item.entity';
import { DashboardCaseRowEntity } from '../../domain/entities/dashboard-case-row.entity';
import { CaseFieldValueEntity } from '../../domain/entities/case-field-value.entity';
import { CaseFieldValueDisplayEntity } from '../../domain/entities/case-field-value-display.entity';
import { CaseStatusSnapshotEntity } from '../../domain/entities/case-status-snapshot.entity';
import { CasePrioritySnapshotEntity } from '../../domain/entities/case-priority-snapshot.entity';
import { ChecklistStepEntity } from '../../domain/entities/checklist-step.entity';
import { CaseMessageEntity } from '../../domain/entities/case-message.entity';
import { WorkNoteEntity } from '../../domain/entities/work-note.entity';
import { CaseAttachmentEntity } from '../../domain/entities/case-attachment.entity';
import { LinkedCaseEntity } from '../../domain/entities/linked-case.entity';
import { ParentChildCasesEntity } from '../../domain/entities/parent-child-cases.entity';
import { ClosingReasonEntity } from '../../domain/entities/closing-reason.entity';
import { ReopeningReasonEntity } from '../../domain/entities/reopening-reason.entity';
import { EmployeeEntity } from '../../domain/entities/employee.entity';
import { CreateCaseCommand, CreateEmployeeCaseCommand } from '../../domain/commands';
import { S3UploadApi } from '@shared';
import { CasesApi } from '../api/cases.api';
import { CreateCaseRequestDto } from '../dtos/create-case-request.dto';
import { CreateEmployeeCaseRequestDto } from '../dtos/create-employee-case-request.dto';
import { UpdateCaseRequestDto } from '../dtos/update-case-request.dto';
import { CasesMapper } from '../mappers/cases.mapper';
import { UpdateChecklistStepRequestDto } from '../dtos/update-checklist-step-request.dto';
import { AddMessageRequestDto } from '../dtos/add-message-request.dto';
import { AddWorkNoteRequestDto } from '../dtos/add-work-note-request.dto';
import { LinkCaseRequestDto } from '../dtos/link-case-request.dto';
import { UpdateCaseAssigneesRequestDto } from '../dtos/update-case-assignees-request.dto';
import { UpdateCaseStatusRequestDto } from '../dtos/update-case-status-request.dto';
import { UpdateCasePriorityRequestDto } from '../dtos/update-case-priority-request.dto';
import { AddInvolvedEmployeesRequestDto } from '../dtos/add-involved-employees-request.dto';
import { UpdateCaseFieldValuesRequestDto } from '../dtos/update-case-field-values-request.dto';
import { TransferToErRequestDto } from '../dtos/transfer-to-er-request.dto';
import {
  AssignableAgentEntity,
  CaseAssignmentOptionsEntity,
} from '../../domain/entities/case-assignment-options.entity';
import type { CaseAssigneeCandidateResponseDto } from '../dtos/case-assignee-candidates-response.dto';

/**
 * Cases Repository
 * Handles all Case data operations: HTTP API calls and DTO mapping.
 */
@Injectable({ providedIn: 'root' })
export class CasesRepository {
  private readonly _api = inject(CasesApi);
  private readonly _s3Api = inject(S3UploadApi);
  private readonly _mapper = inject(CasesMapper);

  /**
   * Retrieves all Cases.
   * @returns Observable with array of Cases
   */
  public getAllCases(): Observable<CaseEntity[]> {
    return this._api.getAllCases().pipe(map((dtos) => this._mapper.toDomainArray(dtos)));
  }

  /**
   * Retrieves a list of all cases (summary data).
   * Used for dropdowns and list views (e.g., Related Cases multiselect).
   * @returns Observable with array of case list items (all data, no pagination)
   */
  public getCasesList(): Observable<CaseListItemEntity[]> {
    return this._api.getCasesList().pipe(
      map((dtos) => this._mapper.toCaseListItemsDomain(dtos))
    );
  }

  /**
   * Retrieves a paginated list of cases with server-side pagination.
   * Used for infinite scroll dropdowns with large datasets.
   * @param params - HTTP parameters including Skip, Take, Sort, Filter
   * @returns Observable with paginated response
   */
  public getCasesListPaginated(params: HttpParams): Observable<{
    data: CaseListItemEntity[];
    totalElements: number;
    totalPages: number;
  }> {
    return this._api.getCasesListPaginated(params).pipe(
      map((response) => ({
        data: this._mapper.toCaseListItemsDomain(response.data),
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      }))
    );
  }

  public getDashboardCases(params: HttpParams): Observable<{
    data: DashboardCaseRowEntity[];
    totalElements: number;
    totalPages: number;
  }> {
    return this._api.getDashboardCases(params).pipe(
      map((response) => ({
        data: this._mapper.toDashboardCaseRowsDomain(response.data),
        totalElements: response.totalElements,
        totalPages: response.totalPages,
      }))
    );
  }

  /**
   * Retrieves a Case by its ID.
   * @param id - Case ID (encrypted UUID string)
   * @returns Observable with the Case entity
   */
  public getCaseById(id: string): Observable<CaseEntity> {
    return this._api.getCaseById(id).pipe(map((dto) => this._mapper.toDomain(dto)));
  }

  /**
   * Retrieves case data optimized for printing.
   * Single API call that fetches case details, field values, and involved employees.
   * Includes automatic audit logging and PII redaction on backend.
   * @param id - Case ID (encrypted)
   * @returns Observable with case entity, field values, and involved employees
   */
  public getCaseForPrint(id: string): Observable<{
    case: CaseEntity;
    fieldValues: CaseFieldValueDisplayEntity[];
    involvedEmployees: EmployeeEntity[];
  }> {
    return this._api.getCaseForPrint(id).pipe(map((dto) => this._mapper.toPrintDomain(dto)));
  }

  /**
   * Creates a new Case.
   * @param command - Case creation command
   * @returns Observable with the created Case response (caseId and caseNumber)
   */
  public createCase(command: CreateCaseCommand): Observable<{ caseId: string; caseNumber: string }> {
    const requestDto: CreateCaseRequestDto = this._mapper.toCreateDto(command);
    return this._api.createCase(requestDto);
  }

  /**
   * Creates a new Case via employee portal endpoint.
   * @param command - Create employee case command
   * @returns Observable with the created Case response (caseId and caseNumber)
   */
  public createEmployeeCase(command: CreateEmployeeCaseCommand): Observable<{ caseId: string; caseNumber: string }> {
    const requestDto: CreateEmployeeCaseRequestDto = this._mapper.toCreateEmployeeDto(command);
    return this._api.createEmployeeCase(requestDto);
  }

  /**
   * Updates an existing Case.
   * @param id - Case ID (encrypted UUID string)
   * @param data - Case update data
   * @returns Observable with the updated Case ID
   */
  public updateCase(id: string, data: Partial<CaseEntity>): Observable<{ id: string }> {
    const requestDto: UpdateCaseRequestDto = this._mapper.toUpdateDto(data);
    return this._api.updateCase(id, requestDto);
  }

  /**
   * Deletes a Case.
   * @param id - Case ID (encrypted UUID string)
   * @returns Observable void
   */
  public deleteCase(id: string): Observable<void> {
    return this._api.deleteCase(id);
  }

  /**
   * Retrieves field values for a Case (minimal entity - for edit mode).
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of CaseFieldValueEntity
   */
  public getCaseFieldValues(caseId: string): Observable<CaseFieldValueEntity[]> {
    return this._api.getCaseFieldValues(caseId).pipe(
      map((dtos) => dtos.map(dto => {
        const entity = new CaseFieldValueEntity();
        entity.versionFieldId = dto.versionFieldId;
        entity.value = dto.value ?? '';
        return entity;
      }))
    );
  }

  /**
   * Retrieves field values for display (readonly mode).
   * Returns rich entity with labels and metadata from API - no separate field-definitions call needed.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of CaseFieldValueDisplayEntity
   */
  public getCaseFieldValuesForDisplay(caseId: string): Observable<CaseFieldValueDisplayEntity[]> {
    return this._api.getCaseFieldValues(caseId).pipe(
      map((dtos) => dtos.map(dto => {
        const entity = new CaseFieldValueDisplayEntity();
        entity.id = dto.id;
        entity.versionFieldId = dto.versionFieldId;
        entity.fieldLabel = dto.fieldLabel;
        entity.fieldType = dto.fieldType;
        entity.value = dto.value;
        entity.isRequired = dto.isRequired;
        entity.isVisibleToRequester = dto.isVisibleToRequester;
        entity.sortOrder = dto.sortOrder;
        entity.optionsJson = dto.optionsJson;
        return entity;
      }))
    );
  }

  /**
   * Retrieves status snapshots for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of status snapshots
   */
  public getStatusSnapshots(caseId: string): Observable<CaseStatusSnapshotEntity[]> {
    return this._api.getStatusSnapshots(caseId).pipe(map((dtos) => this._mapper.toStatusSnapshotsDomain(dtos)));
  }

  /**
   * Retrieves priority snapshots for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of priority snapshots
   */
  public getPrioritySnapshots(caseId: string): Observable<CasePrioritySnapshotEntity[]> {
    return this._api.getPrioritySnapshots(caseId).pipe(map((dtos) => this._mapper.toPrioritySnapshotsDomain(dtos)));
  }

  /**
   * Retrieves checklist steps for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of checklist steps
   */
  public getChecklistSteps(caseId: string): Observable<ChecklistStepEntity[]> {
    return this._api.getChecklistSteps(caseId).pipe(map((dtos) => this._mapper.toChecklistStepsDomain(dtos)));
  }

  /**
   * Updates a checklist step completion status.
   * @param caseId - Case ID (encrypted UUID string)
   * @param stepId - Step ID (encrypted UUID string)
   * @param isCompleted - Completion status
   * @returns Observable void
   */
  public updateChecklistStep(caseId: string, stepId: string, isCompleted: boolean): Observable<void> {
    const request: UpdateChecklistStepRequestDto = { isCompleted };
    return this._api.updateChecklistStep(caseId, stepId, request);
  }

  /**
   * Retrieves messages for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of messages
   */
  public getMessages(caseId: string): Observable<CaseMessageEntity[]> {
    return this._api.getMessages(caseId).pipe(map((dtos) => this._mapper.toMessagesDomain(dtos)));
  }

  /**
   * Adds a message to a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param messageType - Message type
   * @param body - Message body
   * @param attachments - Attachments
   * @returns Observable with message ID
   */
  public addMessage(
    caseId: string,
    messageType: string,
    body: string,
    attachments: CaseAttachmentEntity[]
  ): Observable<{ id: string }> {
    // Convert messageType string to number:
    // 1 = ReplyToRequester (public communication with case requester)
    // 2 = System (automatically generated by the system)
    const messageTypeNumber = messageType.toUpperCase() === 'PUBLIC' ? 1 : 2;

    const request: AddMessageRequestDto = {
      messageType: messageTypeNumber,
      body,
      attachments: attachments.map((a) => ({
        fileName: a.fileName,
        filePath: a.filePath,
        fileSizeBytes: a.fileSizeBytes,
        contentType: a.contentType,
      })),
    };
    return this._api.addMessage(caseId, request);
  }

  /**
   * Marks all unread messages for a case as read by the current user.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable void
   */
  public markMessagesAsRead(caseId: string): Observable<void> {
    return this._api.markMessagesAsRead(caseId);
  }

  /**
   * Retrieves work notes for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of work notes
   */
  public getWorkNotes(caseId: string): Observable<WorkNoteEntity[]> {
    return this._api.getWorkNotes(caseId).pipe(map((dtos) => this._mapper.toWorkNotesDomain(dtos)));
  }

  /**
   * Adds a work note to a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param body - Work note body
   * @param attachments - Attachments
   * @returns Observable with work note ID
   */
  public addWorkNote(caseId: string, body: string, attachments: CaseAttachmentEntity[]): Observable<{ id: string }> {
    const request: AddWorkNoteRequestDto = {
      body,
      attachments: attachments.map((a) => ({
        fileName: a.fileName,
        filePath: a.filePath,
        fileSizeBytes: a.fileSizeBytes,
        contentType: a.contentType,
      })),
    };
    return this._api.addWorkNote(caseId, request);
  }

  /**
   * Retrieves attachments for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of attachments
   */
  public getAttachments(caseId: string): Observable<CaseAttachmentEntity[]> {
    return this._api.getAttachments(caseId).pipe(
      map((dtos) => this._mapper.toAttachmentsDomain(dtos)),
      catchError((error) => this._handleError(error, 'get attachments'))
    );
  }

  /**
   * Upload attachment using 3-step pre-signed URL flow.
   * Step 1: Get upload URL from backend
   * Step 2: Upload to S3 directly using XMLHttpRequest (supports progress)
   * Step 3: Confirm upload to backend
   *
   * Constructs the attachment entity from upload metadata to avoid extra API call
   * and eliminate eventual consistency issues with list endpoint.
   *
   * @param caseId - Case ID (encrypted)
   * @param file - File object from input
   * @param onProgress - Optional progress callback (0-100)
   * @returns Observable with the created attachment entity
   */
  public uploadAttachment(
    caseId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Observable<CaseAttachmentEntity> {
    // Step 1: Get pre-signed upload URL
    return this._api.getAttachmentUploadUrl(caseId, {
      fileName: file.name,
      contentType: file.type,
      fileSizeBytes: file.size
    }).pipe(
      switchMap((uploadResponse) => {
        // Step 2: Upload to S3 using dedicated S3 API (returns Observable)
        return this._s3Api.uploadToS3(
          uploadResponse.uploadUrl,
          file,
          uploadResponse.requiredHeaders,
          onProgress // Pass through progress callback
        ).pipe(
          // Step 3: Confirm upload to backend
          switchMap(() =>
            this._api.confirmAttachmentUpload(caseId, {
              uploadId: uploadResponse.uploadId
            })
          ),
          // Construct entity from metadata we already have (avoids extra API call)
          map((confirmResponse) => this._constructAttachmentEntity(
            confirmResponse.id,
            file.name,
            file.type,
            file.size
          ))
        );
      }),
      catchError((error) => this._handleUploadError(error))
    );
  }

  /**
   * Constructs an attachment entity from upload metadata.
   * Avoids N+1 API calls and eventual consistency issues.
   *
   * Note: uploadedBy fields and uploadedOn are optional in the entity since
   * backend doesn't return user info in confirm response. The list endpoint
   * will have full details when attachments are refreshed, but for immediate
   * state updates this is sufficient. Presentation layer handles rendering
   * when these fields are undefined.
   */
  private _constructAttachmentEntity(
    id: string,
    fileName: string,
    contentType: string,
    fileSizeBytes: number
  ): CaseAttachmentEntity {
    const entity = new CaseAttachmentEntity();
    entity.id = id;
    entity.fileName = fileName;
    entity.contentType = contentType;
    entity.fileSizeBytes = fileSizeBytes;
    entity.filePath = ''; // Not provided by backend in confirm response
    // uploadedByUserId, uploadedByUserName, uploadedOn are optional and undefined here
    return entity;
  }

  /**
   * Get pre-signed download URL for an attachment.
   * URL expires after 60 minutes.
   * @param caseId - Case ID (encrypted)
   * @param attachmentId - Attachment ID (encrypted)
   * @returns Observable with download URL response
   */
  public getAttachmentDownloadUrl(
    caseId: string,
    attachmentId: string
  ): Observable<{ downloadUrl: string; fileName: string; contentType: string }> {
    return this._api.getAttachmentDownloadUrl(caseId, attachmentId).pipe(
      catchError((error) => this._handleError(error, 'download attachment'))
    );
  }

  /**
   * Delete an attachment.
   * Also deletes the file from S3.
   * @param caseId - Case ID (encrypted)
   * @param attachmentId - Attachment ID (encrypted)
   * @returns Observable void
   */
  public deleteAttachment(
    caseId: string,
    attachmentId: string
  ): Observable<void> {
    return this._api.deleteAttachment(caseId, attachmentId).pipe(
      catchError((error) => this._handleError(error, 'delete attachment'))
    );
  }

  /**
   * Classifies upload errors into structured types.
   * Repository returns error types; presentation layer provides user messages.
   * Separates data access from UI concerns.
   */
  private _handleUploadError(error: unknown): Observable<never> {
    let errorType = AttachmentErrorType.UNKNOWN;
    let originalMessage = 'Failed to upload attachment';
    let statusCode: number | undefined;

    // Check if error is AppError (from httpErrorInterceptor)
    if (this._isAppError(error)) {
      originalMessage = error.message;
      statusCode = error.statusCode;
      const code = error.code.toLowerCase();

      // File too large (413)
      if (error.statusCode === 413 || code.includes('size') || code.includes('large')) {
        errorType = AttachmentErrorType.FILE_TOO_LARGE;
      }
      // File type not allowed (HCMI-706)
      else if (code.includes('typenotallowed') || code.includes('type') && code.includes('not')) {
        errorType = AttachmentErrorType.FILE_TYPE_NOT_ALLOWED;
      }
      // Case is closed (409)
      else if (error.statusCode === 409 || code.includes('closed') || code.includes('caseisclosed')) {
        errorType = AttachmentErrorType.CASE_CLOSED;
      }
      // Upload session expired
      else if (code.includes('expired') || code.includes('attachmentuploadexpired')) {
        errorType = AttachmentErrorType.UPLOAD_EXPIRED;
      }
      // Network error (0)
      else if (error.statusCode === 0) {
        errorType = AttachmentErrorType.NETWORK_ERROR;
      }
    }
    // Check if error is standard Error (from S3UploadApi or client-side)
    else if (error instanceof Error) {
      originalMessage = error.message;
      const message = error.message.toLowerCase();

      // S3-specific errors
      if (message.includes('s3 upload failed')) {
        errorType = AttachmentErrorType.S3_UPLOAD_FAILED;
      }
      // Network errors
      else if (message.includes('network error')) {
        errorType = AttachmentErrorType.NETWORK_ERROR;
      }
      // Upload aborted (cancellation)
      else if (message.includes('aborted')) {
        errorType = AttachmentErrorType.UPLOAD_ABORTED;
      }
      // Upload session expired
      else if (message.includes('expired') || message.includes('attachmentuploadexpired')) {
        errorType = AttachmentErrorType.UPLOAD_EXPIRED;
      }
      // File too large
      else if (message.includes('size') || message.includes('large') || message.includes('413')) {
        errorType = AttachmentErrorType.FILE_TOO_LARGE;
      }
      // Case is closed
      else if (message.includes('closed') || message.includes('caseisclosed') || message.includes('409')) {
        errorType = AttachmentErrorType.CASE_CLOSED;
      }
    }

    return throwError(() => new AttachmentError(errorType, originalMessage, statusCode));
  }

  /**
   * Classifies attachment operation errors into structured types.
   * Repository returns error types; presentation layer provides user messages.
   */
  private _handleError(error: unknown, operation: string): Observable<never> {
    let errorType = AttachmentErrorType.UNKNOWN;
    let originalMessage = `Failed to ${operation}`;
    let statusCode: number | undefined;

    // Check if error is AppError (from httpErrorInterceptor)
    if (this._isAppError(error)) {
      originalMessage = error.message;
      statusCode = error.statusCode;

      if (error.statusCode === 0) {
        errorType = AttachmentErrorType.NETWORK_ERROR;
      } else if (error.statusCode === 403) {
        errorType = AttachmentErrorType.PERMISSION_DENIED;
      } else if (error.statusCode === 404) {
        errorType = AttachmentErrorType.NOT_FOUND;
      } else if (error.statusCode === 409) {
        errorType = AttachmentErrorType.CASE_CLOSED;
      }
    }
    // Fallback to standard Error
    else if (error instanceof Error) {
      originalMessage = error.message;
    }

    return throwError(() => new AttachmentError(errorType, originalMessage, statusCode));
  }

  /**
   * Retrieves linked cases for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of linked cases
   */
  public getLinkedCases(caseId: string): Observable<LinkedCaseEntity[]> {
    return this._api.getLinkedCases(caseId).pipe(map((dtos) => this._mapper.toLinkedCasesDomain(dtos)));
  }

  /**
   * Retrieves the parent and children for a Case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with ParentChildCasesEntity
   */
  public getParentChildCases(caseId: string): Observable<ParentChildCasesEntity> {
    return this._api.getParentChildCases(caseId).pipe(
      map((dto) => this._mapper.toParentChildCasesDomain(dto))
    );
  }

  /**
   * Links existing cases as children of the current case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param childCaseIds - Array of encrypted case IDs to add as children
   * @returns Observable void
   */
  public addChildCases(caseId: string, childCaseIds: string[]): Observable<void> {
    return this._api.addChildCases(caseId, childCaseIds);
  }

  /**
   * Sets the parent case for a case.
   * @param caseId - Case ID (encrypted UUID string) of the child
   * @param parentCaseId - Case ID (encrypted UUID string) of the parent
   * @returns Observable void
   */
  public setParentCase(caseId: string, parentCaseId: string): Observable<void> {
    return this._api.setParentCase(caseId, parentCaseId);
  }

  /**
   * Removes a parent/child relationship between cases.
   * @param caseId - Case ID (encrypted UUID string)
   * @param linkedCaseId - Encrypted ID of the related (parent or child) case to unlink
   * @returns Observable void
   */
  public unlinkParentChild(caseId: string, linkedCaseId: string): Observable<void> {
    return this._api.unlinkParentChild(caseId, linkedCaseId);
  }

  /**
   * Links a case to another case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param linkedCaseId - Linked case ID (encrypted UUID string)
   * @param linkType - Link type
   * @returns Observable void
   */
  public linkCase(caseId: string, linkedCaseId: string, linkType: string): Observable<void> {
    const request: LinkCaseRequestDto = {
      linkedCaseId,
      linkType,
    };
    return this._api.linkCase(caseId, request);
  }

  /**
   * Unlinks a case from another case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param linkedCaseId - Linked case ID to unlink (encrypted UUID string)
   * @returns Observable void
   */
  public unlinkCase(caseId: string, linkedCaseId: string): Observable<void> {
    return this._api.unlinkCase(caseId, linkedCaseId);
  }

  public updateLinkType(caseId: string, linkedCaseId: string, linkType: 'parent' | 'child' | 'related'): Observable<void> {
    return this._api.updateLinkType(caseId, linkedCaseId, linkType);
  }

  /**
   * Get all active closing reasons.
   * @returns Observable array of ClosingReasonEntity
   */
  public getClosingReasons(): Observable<ClosingReasonEntity[]> {
    return this._api.getClosingReasons().pipe(
      map((dtos) => this._mapper.toClosingReasonEntities(dtos))
    );
  }

  /**
   * Close a case with reason and notes.
   * @param caseId - Encrypted case ID
   * @param command - Close case command with statusId, closingReasonId, closingNotes
   * @returns Observable void
   */
  public closeCase(
    caseId: string,
    command: { statusId: string; closingReasonId: string; closingNotes: string }
  ): Observable<void> {
    const dto = this._mapper.toCloseCaseRequestDto(command);
    return this._api.closeCase(caseId, dto);
  }

  /**
   * Get all active reopening reasons.
   * @returns Observable array of ReopeningReasonEntity
   */
  public getReopeningReasons(): Observable<ReopeningReasonEntity[]> {
    return this._api.getReopeningReasons().pipe(
      map((dtos) => this._mapper.toReopeningReasonEntities(dtos))
    );
  }

  /**
   * Reopen a closed case with reason and notes.
   * @param caseId - Encrypted case ID
   * @param command - Reopen case command with reopeningReasonId, reopeningNotes
   * @returns Observable void
   */
  public reopenCase(
    caseId: string,
    command: { reopeningReasonId: string; reopeningNotes: string }
  ): Observable<void> {
    const dto = this._mapper.toReopenCaseRequestDto(command);
    return this._api.reopenCase(caseId, dto);
  }

  /**
   * Updates case assignees.
   * @param caseId - Case ID (encrypted UUID string)
   * @param primaryAssigneeUserId - Primary assignee user ID
   * @param secondaryAssigneeUserId - Secondary assignee user ID
   * @returns Observable void
   */
  public updateCaseAssignees(
    caseId: string,
    primaryAssigneeUserId: string | null,
    secondaryAssigneeUserId: string | null
  ): Observable<void> {
    const request: UpdateCaseAssigneesRequestDto = {
      primaryAssigneeUserId,
      secondaryAssigneeUserId,
    };
    return this._api.updateCaseAssignees(caseId, request);
  }

  public getWatcherOptions(caseId: string): Observable<EmployeeEntity[]> {
    return this._api.getWatcherOptions(caseId).pipe(
      map((dtos) => this._mapper.watcherOptionsToEmployeesDomain(dtos))
    );
  }

  public getCaseAssigneeCandidates(caseId: string): Observable<AssignableAgentEntity[]> {
    return this._api.getCaseAssigneeCandidates(caseId).pipe(
      map((dtos) => dtos.map((dto) => this._mapCandidateDto(dto)))
    );
  }

  public getBulkCaseAssignmentOptions(caseIds: string[]): Observable<AssignableAgentEntity[]> {
    return this._api.getBulkCaseAssignmentOptions(caseIds).pipe(
      map((dtos) => dtos.map((dto) => this._mapCandidateDto(dto)))
    );
  }

  private _mapCandidateDto(dto: CaseAssigneeCandidateResponseDto): AssignableAgentEntity {
    const agent = new AssignableAgentEntity();
    agent.userId = dto.userId;
    agent.firstName = dto.firstName;
    agent.lastName = dto.lastName;
    agent.email = dto.email;
    agent.isManager = dto.isManager;
    agent.teamIds = dto.teamIds ?? [];
    return agent;
  }

  public getCaseTypeAssignmentOptions(caseTypeId: string, search?: string): Observable<CaseAssignmentOptionsEntity> {
    return this._api.getCaseTypeAssignmentOptions(caseTypeId, search).pipe(
      map((dto) => {
        const entity = new CaseAssignmentOptionsEntity();
        entity.caseTypeId = dto.caseTypeId;
        entity.ruleKind = dto.ruleKind;
        entity.teamId = dto.teamId;
        entity.suggestedPrimaryUserId = dto.suggestedPrimaryUserId;
        entity.allowsPrimaryEdit = dto.allowsPrimaryEdit;
        entity.allowsSecondary = dto.allowsSecondary;
        entity.candidateAgents = (dto.candidateAgents ?? []).map((a) => {
          const agent = new AssignableAgentEntity();
          agent.userId = a.userId;
          agent.firstName = a.firstName;
          agent.lastName = a.lastName;
          agent.email = a.email;
          agent.isManager = a.isManager;
          return agent;
        });
        return entity;
      })
    );
  }

  /**
   * Updates case status.
   * @param caseId - Case ID (encrypted UUID string)
   * @param statusId - Status ID (UUID string)
   * @returns Observable void
   */
  public updateCaseStatus(caseId: string, statusId: string): Observable<void> {
    const request: UpdateCaseStatusRequestDto = {
      statusId,
    };
    return this._api.updateCaseStatus(caseId, request);
  }

  /**
   * Updates case priority.
   * @param caseId - Case ID (encrypted UUID string)
   * @param priorityCode - Priority code (e.g., "high", "medium", "low")
   * @returns Observable void
   */
  public updateCasePriority(caseId: string, priorityCode: string): Observable<void> {
    const request: UpdateCasePriorityRequestDto = {
      priorityCode,
    };
    return this._api.updateCasePriority(caseId, request);
  }

  /**
   * Retrieves involved employees for a case.
   * @param caseId - Case ID (encrypted UUID string)
   * @returns Observable with array of employee entities
   */
  public getInvolvedEmployees(caseId: string): Observable<EmployeeEntity[]> {
    return this._api.getInvolvedEmployees(caseId).pipe(map((dtos) => this._mapper.toEmployeesDomain(dtos)));
  }

  /**
   * Adds involved employees to a case (bulk operation).
   * @param caseId - Case ID (encrypted UUID string)
   * @param employeeIds - Array of employee user IDs to add
   * @returns Observable void
   */
  public addInvolvedEmployees(caseId: string, employeeIds: string[]): Observable<void> {
    const request: AddInvolvedEmployeesRequestDto = { userIds: employeeIds };
    return this._api.addInvolvedEmployees(caseId, request);
  }

  /**
   * Removes a single involved employee from a case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param userId - User ID to remove (encrypted UUID string)
   * @returns Observable void
   */
  public removeInvolvedEmployee(caseId: string, userId: string): Observable<void> {
    return this._api.removeInvolvedEmployee(caseId, userId);
  }

  /**
   * Updates field values for a case.
   * @param caseId - Case ID (encrypted UUID string)
   * @param fieldValues - Array of field value entities
   * @returns Observable void
   */
  public updateCaseFieldValues(caseId: string, fieldValues: CaseFieldValueEntity[]): Observable<void> {
    const request: UpdateCaseFieldValuesRequestDto = {
      fieldValues: fieldValues.map(fv => ({
        versionFieldId: fv.versionFieldId,
        value: fv.value || '',
      }))
    };
    return this._api.updateCaseFieldValues(caseId, request);
  }

  /**
   * Transfers a case to Employee Relations (ER).
   * This action closes the case in Case Management and transfers ownership to ER team.
   * @param caseId - The ID of the case to transfer
   * @param closingNotes - Notes documenting the reason for transfer
   * @returns Observable void (204 No Content on success)
   */
  public transferToEr(caseId: string, closingNotes: string): Observable<void> {
    const request: TransferToErRequestDto = {
      closingNotes
    };
    return this._api.transferToEr(caseId, request);
  }

  /**
   * Sets watch status for a case.
   * Allows user to watch/unwatch a case to receive notifications.
   * @param caseId - The ID of the case
   * @param isWatching - Whether to watch (true) or unwatch (false) the case
   * @returns Observable void (204 No Content on success)
   */
  public setWatchCase(caseId: string, isWatching: boolean): Observable<void> {
    return this._api.setWatchCase(caseId, isWatching);
  }

  /**
   * Type guard to check if error is AppError (from httpErrorInterceptor).
   * HttpClient failures are transformed by httpErrorInterceptor into AppError objects.
   *
   * Validates that error object has all required properties with correct types.
   * Broken into smaller functions for readability and maintainability.
   */
  private _isAppError(error: unknown): error is AppError {
    if (!this._isObject(error)) {
      return false;
    }

    if (!this._hasRequiredProperties(error)) {
      return false;
    }

    return this._hasCorrectPropertyTypes(error);
  }

  /**
   * Checks if value is a non-null object.
   */
  private _isObject(value: unknown): value is Record<string, unknown> {
    return value !== null && typeof value === 'object';
  }

  /**
   * Checks if error object has required AppError properties.
   */
  private _hasRequiredProperties(error: Record<string, unknown>): boolean {
    return 'code' in error && 'message' in error && 'statusCode' in error;
  }

  /**
   * Validates that AppError properties have correct types.
   */
  private _hasCorrectPropertyTypes(error: Record<string, unknown>): boolean {
    const candidate = error as { code: unknown; message: unknown; statusCode: unknown };
    return (
      typeof candidate.code === 'string' &&
      typeof candidate.message === 'string' &&
      typeof candidate.statusCode === 'number'
    );
  }
}
